import { MUX } from "../../../constants/componentType";
import { getNum } from "../../helper/getSignalType";
import { getComponentByName, getConnectedPinNumber, SERDES_TYPES } from "../../PCBHelper";
import { getCompPinsNetByCompName } from "../../PCBHelper/net";
import { getSignalElementType } from "./signalsIdentification";

function judgePinNameGeneratePinsConnections(pinList) {
  let pinsConnection = [], isGenerate = false;
  const pinIN = pinList.filter(item => item.mName.match(/(IN)|(INPUT)/ig));
  const pinOUT = pinList.filter(item => item.mName.match(/(OUT)|(OUTPUT)/ig));
  if (pinIN.length === pinOUT.length && pinIN.length > 0) {
    isGenerate = true;
    for (let item of pinIN) {
      const number = getNum(item.mName);
      const findPinMap = pinOUT.find(item => getNum(item.mName) === number);
      pinsConnection.push({ pin: item.mNumber, pinMap: findPinMap ? findPinMap.mNumber : "" })
    }

    const filterPinOut = pinOUT.filter(item => !pinsConnection.find(it => it.pinMap === item.mNumber));

    if (filterPinOut.length === 1) {
      pinsConnection.forEach(item => {
        if (!item.pinMap) {
          item.pinMap = filterPinOut[0].mNumber;
        }
      })
    }

    if (filterPinOut.length === 2) {
      pinsConnection.forEach((item, index) => {
        const pinMap = getConnectedPinNumber({
          pinNum: item.pin,
          pinLength: pinList.length
        });
        item.pinMap = pinMap ? pinMap.toString() : filterPinOut[index];
      })
    }
  }

  return { pinsConnection, isGenerate };
}

/**
 * Create pinsConnection according to signal.pins
 * @param {Array} signalPinList
 * @returns 
 */
function getMUXPinsConnection({ designId, compName, signalPinList }) {
  const comp = getComponentByName(designId, compName);
  const compPinList = comp && comp.mPart && comp.mPart.mPinList ? comp.mPart.mPinList : [];

  const pinList = signalPinList.map(pin => {
    const pinInfo = compPinList.find(p => p.mName === pin.pin);
    return { ...pinInfo, signal: pin.signal, net: pin.net };
  })

  const { pinsConnection: _pinsConnection, isGenerate } = judgePinNameGeneratePinsConnections(pinList);
  if (isGenerate) {
    return { pinsConnection: _pinsConnection, isUpdateNet: false };
  }

  let pinsConnection = [], isUpdateNet = false;
  const signalMap = new Map(), pinIN = [], pinOUT = [];
  signalPinList.forEach(item => {
    if (!signalMap.has(item.signal)) {
      signalMap.set(item.signal, signalMap.size + 1);
    }
  });
  signalMap.forEach((value, key) => {
    if (value % 2 === 1) {
      pinIN.push(...pinList.filter(item => item.signal === key));
    } else {
      pinOUT.push(...pinList.filter(item => item.signal === key));
    }
  })

  // in_pin and out_pin cannot come from the same signal. If more than one, find them from comPinList
  const _pinINList = pinIN.map(item => item.mNumber);
  const _pinOUTList = pinOUT.map(item => item.mNumber);
  const usedPinList = [];
  let emptyPinMaps = []; // {pin,net, equalNumberMap: {prefixIndex, pinMap}}
  for (let i = 0; i < _pinINList.length; i++) {
    const _pinMapNum = getConnectedPinNumber({
      pinNum: _pinINList[i],
      pinLength: pinList.length
    });
    let _pinMap = _pinMapNum ? _pinMapNum.toString() : "";
    if (!_pinOUTList.includes(_pinMap)) {
      const index = i;
      // Determine whether pinOUTList[index] is in use, and if so, look for other unused pinNums
      if (!usedPinList.includes(_pinOUTList[index])) {
        _pinMap = _pinOUTList[index] ? _pinOUTList[index] : "";
      } else {
        _pinMap = _pinOUTList.find(it => !usedPinList.includes(it));
      }
    }
    if (!_pinMap) {
      emptyPinMaps.push({ pin: _pinINList[i], net: pinIN[i].net, equalNumberMap: new Map() });
    } else {
      usedPinList.push(_pinMap);
    }
    pinsConnection.push({ pin: _pinINList[i], pinMap: _pinMap });
  }

  if (emptyPinMaps.length > 0) {
    isUpdateNet = true;
    const _compPinList = compPinList
      .map(item => item.mNumber)
      .filter(it => !_pinOUTList.includes(it) && !_pinINList.includes(it));
    const compPinsNet = getCompPinsNetByCompName(designId, compName);

    /**
     * Find the number of characters that match the position
     * str1: CPHY_TEST1_A0
     * str2: CPHY_TEST2_A0
     * => 12
     * @param {string} str1 
     * @param {string} str2 
     * @returns 
     */
    function getSameEqualNumber(str1, str2) {
      let equalNumber = 0;
      const minLength = Math.min(str1.length, str2.length);
      for (let i = 0; i < minLength; i++) {
        if (str1[i] === str2[i]) equalNumber++
      }
      return equalNumber;
    }

    for (const compPin of _compPinList) {
      const netName = (compPinsNet[compPin] || {}).mName || "";
      emptyPinMaps = emptyPinMaps.map(emp => {
        const equalNumber = getSameEqualNumber(netName, emp.net);
        if (equalNumber > -1 && !emp.equalNumberMap.has(equalNumber)) {
          emp.equalNumberMap.set(equalNumber, compPin);
        }
        return emp;
      })
    }

    pinsConnection = pinsConnection.map(pc => {
      if (!pc.pinMap) {
        const emp = emptyPinMaps.find(emp => emp.pin === pc.pin);
        let pinMap = null;
        while (!pinMap) {
          const key = Math.max(...emp.equalNumberMap.keys());
          if (!usedPinList.includes(emp.equalNumberMap.get(key))) {
            pinMap = emp.equalNumberMap.get(key);
          } else {
            emp.equalNumberMap.delete(key);
          }
        }
        return { ...pc, pinMap };
      }
      return pc;
    })
  }

  return { pinsConnection, isUpdateNet };
}

// after select net, find pinMap
function getMUXPinsConnectionByNewPins({ designId, compName, signalPinList }) {
  const comp = getComponentByName(designId, compName);
  const compPinList = comp && comp.mPart && comp.mPart.mPinList ? comp.mPart.mPinList : [];

  const pinList = signalPinList.map(pin => {
    const pinInfo = compPinList.find(p => p.mName === pin.pin);
    return { ...pinInfo, signal: pin.signal };
  })

  const { pinsConnection: _pinsConnection, isGenerate } = judgePinNameGeneratePinsConnections(pinList);
  if (isGenerate) {
    return { pinsConnection: _pinsConnection, isUpdateNet: false };
  }

  let pinsConnection = [], isUpdateNet = false;
  const _pinList = pinList.map(item => item.mNumber).sort((a, b) => Number(a) - Number(b));
  for (let i = 0; i < _pinList.length / 2; i++) {
    const _pinMapNum = getConnectedPinNumber({
      pinNum: _pinList[i],
      pinLength: _pinList.length
    });
    let _pinMap = _pinMapNum ? _pinMapNum.toString() : "";
    if (!_pinList.includes(_pinMap)) {
      const index = _pinList.length - i - 1;
      _pinMap = _pinList[index] ? _pinList[index] : "";
    }
    pinsConnection.push({ pin: _pinList[i], pinMap: _pinMap });
  }
  return { pinsConnection, isUpdateNet };
}

// after select net
function updateMUXPinConnection(components = [], designId) {
  let modifiedCompNames = [];
  const newComps = components.map(comp => {
    if (comp.type === MUX) {
      const pinNames = comp.pins.map(pin => pin.pin);
      const _pinConnection = (comp.pinsConnection || []).filter(pc => pinNames.includes(pc.pin) && pinNames.includes(pc.pinMap) && pc.pin !== pc.pinMap);
      const newPins = comp.pins.filter(pin => !_pinConnection.find(pc => pc.pin === pin.pin || pc.pinMap === pin.pin));
      if (newPins.length) {
        const { pinsConnection: newPinConnection, isUpdateNet } = getMUXPinsConnectionByNewPins({ designId, compName: comp.name, signalPinList: newPins });

        if (isUpdateNet) {
          modifiedCompNames.push(comp.name);
        }
        return {
          ...comp,
          pinsConnection: _pinConnection.concat(newPinConnection)
        }
      }
    }
    return comp;
  })

  return {
    newComps,
    modifiedComponents: newComps.filter(it => modifiedCompNames.includes(it.name))
  }
}

// remove unusable signal
function removeUnusableSignal(channelInfo) {
  const content = channelInfo.content || {},
    components = content.components || [],
    signals = content.signals || [],
    serdesType = channelInfo.type;
  const compPins = components.filter(it => SERDES_TYPES.includes(it.type)).map(comp => comp.pins).flat();
  const signalToPinMap = new Map();
  for (const signal of signals) {
    if (signalToPinMap.get(signal.name)) {
      continue;
    }
    const nets = getSignalElementType(serdesType).map(item => signal[item] || []).flat(2);
    const pin = compPins.find(p => (p.signal === signal.name || !p.signal) && nets.includes(p.net));
    signalToPinMap.set(signal.name, pin);
  }

  return signals.filter(signal => !signalToPinMap.get(signal.name)).map(s => s.name);
}

export {
  getMUXPinsConnection,
  updateMUXPinConnection,
  removeUnusableSignal
}